<template>
  <b-card
    class="pricing-card"
    :class="{
      'pricing-card--current': isCurrentVariant,
      'pricing-card--full-width': product.useSingleColumn,
      'h-100': !product.useSingleColumn,
    }"
    :style="{ '--tier-color': product.color }"
  >
    <div class="pricing-card__accent"></div>

    <div class="pricing-card__main">
      <div class="pricing-card__header py-3">
        <h5 class="pricing-card__name mb-3">{{ product.name }}</h5>

        <!-- Interval Toggle -->
        <TogglePills
          v-if="intervalOptions.length > 1"
          v-model="selectedInterval"
          :options="intervalOptions"
          :accent-color="product.color"
        />
      </div>

      <div
        class="pricing-card__actions mb-4"
        :class="{
          'order-md-3': product.useSingleColumn,
          'order-md-2': !product.useSingleColumn,
        }"
      >
        <div
          v-if="!product.useSingleColumn && shortDescriptionContent"
          class="pricing-card__description"
          v-html="shortDescriptionContent"
        ></div>
        <div class="pricing-card__price mb-3">
          <template v-if="salePrice">
            <span class="pricing-card__amount pricing-card__amount--original">${{ currentPrice }}</span>
            <span class="pricing-card__amount pricing-card__amount--sale">${{ salePrice }}</span>
          </template>
          <span v-else class="pricing-card__amount">${{ currentPrice }}</span>
          <span class="pricing-card__suffix">{{
            $t(`billing.subscriptions.intervalSuffix.${selectedInterval}`)
          }}</span>
        </div>

        <button
          v-if="!customButton"
          class="pricing-card__button"
          :class="{ 'pricing-card__button--disabled': isCurrentVariant }"
          :disabled="isCurrentVariant || loading || disabled"
          @click="handleSelect"
        >
          <b-spinner v-if="loading" small class="mr-2" />
          {{ buttonText }}
        </button>
        <slot name="custom-button"></slot>

        <div v-if="disabledReason" class="pricing-card__notice mt-2 text-center mb-0">
          <div v-html="disabledReason" />
        </div>
      </div>

      <div
        class="pricing-card__description mb-4"
        :class="{
          'order-md-2': product.useSingleColumn,
          'order-md-3': !product.useSingleColumn,
        }"
        v-html="descriptionContent"
      ></div>
    </div>
  </b-card>
</template>

<script>
import { mapState } from "vuex";
import StripeMixin from "@/mixins/StripeMixin";
import TogglePills from "@/components/elements/TogglePills.vue";
import ServerContentMixin from "@/mixins/ServerContentMixin";

export default {
  components: { TogglePills },
  mixins: [StripeMixin, ServerContentMixin],

  props: {
    product: { type: Object, required: true },
    defaultInterval: { type: Number, default: 0 },
    availableIntervals: { type: Array, default: () => [0, 2] },
    disabled: { type: Boolean, default: false },
    disabledReason: { type: String, default: null },
    customDescriptionKey: { type: String, default: null },
    customShortDescriptionKey: { type: String, default: null },
    customButton: { type: Boolean, default: false },
  },

  data() {
    return {
      selectedInterval: this.defaultInterval,
      loading: false,
    };
  },

  computed: {
    ...mapState({
      subscriptions: (state) => state.account.subscriptions,
      products: (state) => state.checkout.products,
    }),

    currentSubscription() {
      const nonLegacyKeys =
        this.products.subscriptions?.filter((x) => !x.isLegacy).map((x) => x.key) || [];
      return (
        this.subscriptions?.find((sub) =>
          sub.products?.some((p) => nonLegacyKeys.includes(p.key))
        ) || null
      );
    },

    hasLegacySubscription() {
      return (
        this.subscriptions?.some((sub) => sub.products?.some((x) => x.isLegacy)) || false
      );
    },

    intervalOptions() {
      return this.availableIntervals
        .filter((x) => this.product.intervals[x] !== undefined)
        .map((key) => ({
          value: key,
          label: this.$t(`billing.subscriptions.intervalNames.${key}`),
        }));
    },

    currentPrice() {
      return this.product.intervals[this.selectedInterval]?.price || 0;
    },

    salePrice() {
      return this.product.intervals[this.selectedInterval]?.salePrice || 0;
    },

    currentPriceId() {
      return this.product.intervals[this.selectedInterval]?.stripePriceId;
    },

    isCurrentVariant() {
      if (!this.currentSubscription?.plans) return false;
  
      const product = this.product.intervals[this.selectedInterval] || { historicalPrices: [] };
      
      return this.currentSubscription.plans.includes(this.currentPriceId) 
        || product.historicalPrices?.some(x => this.currentSubscription.plans.includes(x));
    },

    hasSubscription() {
      return !!this.currentSubscription;
    },

    buttonText() {
      const nameAndInterval = `${this.product.name}`;
      if (this.loading) return this.$t("billing.subscriptions.processing");
      if (this.isCurrentVariant)
        return `${this.$t("billing.subscriptions.currentPlan")} ${nameAndInterval}`;
      if (this.hasSubscription || this.hasLegacySubscription)
        return `${this.$t("billing.subscriptions.changePlan")} ${nameAndInterval}`;
      return this.$t("billing.checkout.subscribe");
    },

    descriptionContent() {
      if (this.customDescriptionKey) return this.getByKey(this.customDescriptionKey);
      return this.getByKey(`pricing.table.${this.product.key}.long`);
    },

    shortDescriptionContent() {
      if (this.customShortDescriptionKey)
        return this.getByKey(this.customShortDescriptionKey);
      const key = `pricing.table.${this.product.key}.short`;
      const desc = this.getByKey(key);
      if (desc === key) return null;
      return desc;
    },
  },

  methods: {
    handleSelect() {
      if (this.isCurrentVariant || this.loading) return;

      // Emit event with selected interval
      this.$emit("select", {
        productKey: this.product.key,
        variant: this.selectedInterval,
      });

      // If custom button is used, don't handle internally
      if (this.customButton) return;

      this.loading = true;
      const payload = { productKey: this.product.key, variant: this.selectedInterval };

      if (this.hasSubscription || this.hasLegacySubscription) {
        // Existing subscription - go to customer portal to change
        this.createCustomerPortal(payload).finally(() => {
          this.loading = false;
        });
      } else {
        // New subscription - create checkout session
        this.axios
          .post("/billing/create-checkout-session", payload)
          .then((res) => {
            window.location.href = res.data.url;
          })
          .catch(() => {
            this.$notify({
              type: "error",
              title: this.$t("errors.general"),
              text: this.$t("errors.retry"),
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pricing-card {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  :deep(.card-body) {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding-top: 15px;
    padding-bottom: 15px;
  }

  :deep(.pricing-card__description) {
    li {
      padding: 2.5px 8px;
    }
    ul {
      margin-bottom: 1rem;
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    margin-top: auto;
  }

  &--current {
    opacity: 0.7;
  }

  // Full width variant
  &--full-width {
    grid-column: 1 / -1;

    :deep(.card-body) {
      flex-direction: row;
      align-items: stretch;
      gap: 2.5rem;
      padding: 1.75rem;

      @media (max-width: 991px) {
        flex-direction: column;
        align-items: stretch;
        gap: 1.5rem;
      }
    }

    .pricing-card__main {
      flex-direction: row;
      align-items: flex-start;
      gap: 2.5rem;
      flex: 1;

      @media (max-width: 991px) {
        flex-direction: column;
        gap: 1rem;
      }
    }

    .pricing-card__header {
      flex-shrink: 0;
      min-width: 200px;
      display: flex;
      flex-direction: row !important;

      .pricing-card__name {
        margin-bottom: 1rem !important;
      }

      :deep(.toggle-pills) {
        margin-bottom: 0 !important;
      }
    }

    .pricing-card__description {
      flex: 1;
      margin-bottom: 0 !important;
      padding-top: 0.25rem;

      @media (max-width: 991px) {
        margin-bottom: 0.5rem !important;
        padding-top: 0;
      }
    }

    .pricing-card__actions {
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      gap: 0.75rem;
      margin-top: 0;
      flex-shrink: 0;
      min-width: 160px;
      border-left: 1px solid rgba(128, 128, 128, 0.15);
      padding-left: 2rem;

      @media (max-width: 991px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        border-left: none;
        border-top: 1px solid rgba(128, 128, 128, 0.15);
        padding-left: 0;
        padding-top: 1rem;
        min-width: unset;
      }

      .pricing-card__price {
        margin-bottom: 0 !important;
        white-space: nowrap;
      }

      .pricing-card__button {
        white-space: nowrap;
        width: auto;
        min-width: 140px;
      }
    }

    .pricing-card__amount {
      font-size: 22px;
    }
  }

  &__accent {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: var(--tier-color);
  }

  &__name {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    color: var(--tier-color);
  }

  &__price {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  &__amount {
    font-size: 18px;
    font-weight: 600;

    &--original {
      text-decoration: line-through;
      opacity: 0.5;
      font-size: 14px;
      font-weight: 400;
      margin-right: 6px;
    }

    &--sale {
      color: var(--tier-color);
    }
  }

  &__suffix {
    font-size: 13px;
    opacity: 0.6;
  }

  &__description {
    font-size: 13px;
    opacity: 0.8;
    flex-grow: 1;
    text-align: left;

    :deep(ul) {
      padding-left: 1.25rem;
      margin: 0;
    }

    :deep(li) {
      margin-bottom: 0.25rem;
    }
  }

  &__button {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.6rem 1.5rem;
    width: 100%;
    background: var(--tier-color);
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    border-radius: 5px 15px;
    cursor: pointer;
    transition: filter 0.2s ease;

    &:hover:not(:disabled) {
      filter: brightness(90%);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--disabled {
      background: transparent;
      border: 1px solid var(--tier-color);
      color: var(--tier-color);
    }
  }

  &__notice {
    font-size: 12px;
    color: #c25354;
    opacity: 1;

    :deep(a) {
      color: darken(#c25354, 10%);
      text-decoration: underline!important;
    }

    &--current {
      color: #13ac82;
    }
  }
}
</style>
